import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search, Plus, UploadCloud, Wallet, Layers, ListChecks, RefreshCw, X, Trash2, CheckSquare,
  Download, FileArchive, ShieldCheck, Sparkles, Copy, TrendingUp, ScanSearch, Target, Clock,
  Link2,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import CardTile from "../components/CardTile";
import MissingCardTile, { normalizeNumber } from "../components/MissingCardTile";
import ChecklistPanel from "../components/ChecklistPanel";
import ChaseDeckStrip from "../components/ChaseDeckStrip";
import SalesLedgerStrip from "../components/SalesLedgerStrip";
import ManualAddDialog from "../components/ManualAddDialog";
import ShowcaseDialog from "../components/ShowcaseDialog";
import AnimatedNumber from "../components/dashboard/AnimatedNumber";
import StatCard from "../components/dashboard/StatCard";
import InsightsPanel from "../components/dashboard/InsightsPanel";
import DuplicatesPanel from "../components/dashboard/DuplicatesPanel";
import AiSaysCard from "../components/dashboard/AiSaysCard";
import TopMoversStrip from "../components/dashboard/TopMoversStrip";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { cardTotalValue } from "../lib/conditions";

const DAY_MS = 86400000;

export default function Binder() {
  const { checkAuth } = useAuth();
  const { format } = useCurrency();
  const [cards, setCards] = useState([]);
  const [collections, setCollections] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [gameFilter, setGameFilter] = useState("all");
  const [collectionFilter, setCollectionFilter] = useState("all");
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [quickFilter, setQuickFilter] = useState(null); // duplicates | verified | recent
  const [sortBy, setSortBy] = useState("newest");
  const [ownedOnly, setOwnedOnly] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState("");
  const [showNewCollection, setShowNewCollection] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [rescanning, setRescanning] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [downloadingScans, setDownloadingScans] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const filtersRef = useRef(null);

  const loadAll = async () => {
    const [cardsRes, collectionsRes, statsRes] = await Promise.all([
      api.get("/cards"),
      api.get("/collections"),
      api.get("/stats"),
    ]);
    setCards(cardsRes.data);
    setCollections(collectionsRes.data);
    setStats(statsRes.data);
    setLoading(false);
  };

  useEffect(() => {
    loadAll();
  }, []);

  // Unverified web match (excluding fully-manual cards and matches the user handled) or low AI
  // confidence on a card the user hasn't confirmed yet.
  const needsAttention = (c) =>
    (c.id_confidence === "low" && c.status !== "confirmed") || c.status === "pending_review" ||
    (!c.match_verified && c.price_source !== "manual" &&
      !["user_uploaded", "user_removed"].includes(c.reference_image_source));

  const filtered = useMemo(() => {
    const now = Date.now();
    const base = cards.filter((c) => {
      if (gameFilter !== "all" && c.game_type !== gameFilter) return false;
      if (collectionFilter !== "all" && (c.collection_id || "uncategorized") !== collectionFilter) return false;
      if (attentionOnly && !needsAttention(c)) return false;
      if (quickFilter === "duplicates" && (c.quantity || 1) < 2) return false;
      if (quickFilter === "verified" && !c.match_verified) return false;
      if (quickFilter === "recent" && (!c.created_at || now - new Date(c.created_at).getTime() > 7 * DAY_MS)) return false;
      if (search && !c.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
    const sorters = {
      newest: (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0),
      value_desc: (a, b) => (cardTotalValue(b) || 0) - (cardTotalValue(a) || 0),
      value_asc: (a, b) => (cardTotalValue(a) || 0) - (cardTotalValue(b) || 0),
      name: (a, b) => (a.name || "").localeCompare(b.name || ""),
    };
    return [...base].sort(sorters[sortBy] || sorters.newest);
  }, [cards, gameFilter, collectionFilter, search, attentionOnly, quickFilter, sortBy]);

  const attentionCount = useMemo(() => cards.filter(needsAttention).length, [cards]);

  const metrics = useMemo(() => {
    const totalQty = cards.reduce((s, c) => s + (c.quantity || 1), 0);
    const dupEntries = cards.filter((c) => (c.quantity || 1) > 1);
    const dupCount = dupEntries.reduce((s, c) => s + (c.quantity || 1) - 1, 0);
    const dupValue = dupEntries.reduce((s, c) => {
      const per = (cardTotalValue(c) || 0) / (c.quantity || 1);
      return s + per * ((c.quantity || 1) - 1);
    }, 0);
    const now = Date.now();
    const week = cards.filter((c) => c.created_at && now - new Date(c.created_at).getTime() < 7 * DAY_MS);
    const today = week.filter((c) => now - new Date(c.created_at).getTime() < DAY_MS);
    const weekValue = week.reduce((s, c) => s + (cardTotalValue(c) || 0), 0);
    const pendingSales = cards.filter((c) => c.sale_status);
    const pendingValue = pendingSales.reduce((s, c) => s + (cardTotalValue(c) || 0), 0);
    return {
      totalQty, unique: cards.length, dupEntries, dupCount,
      dupValue: Math.round(dupValue * 100) / 100,
      weekCount: week.length, todayCount: today.length,
      weekValue: Math.round(weekValue * 100) / 100,
      pendingCount: pendingSales.length,
      pendingValue: Math.round(pendingValue * 100) / 100,
    };
  }, [cards]);

  const growth = useMemo(() => {
    if (metrics.weekCount === 0) return null;
    if (metrics.weekValue > 0) return { label: `+${format(metrics.weekValue)} this week`, sub: `${metrics.weekCount} card${metrics.weekCount > 1 ? "s" : ""} added` };
    if (metrics.todayCount > 0) return { label: `+${metrics.todayCount} card${metrics.todayCount > 1 ? "s" : ""} today`, sub: "added to your collection" };
    return { label: `+${metrics.weekCount} card${metrics.weekCount > 1 ? "s" : ""} this week`, sub: "added to your collection" };
  }, [metrics, format]);

  const goReviewAttention = () => {
    setAttentionOnly(true);
    setTimeout(() => filtersRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  const goDuplicates = () => {
    setQuickFilter("duplicates");
    setTimeout(() => filtersRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  const insights = useMemo(() => {
    const list = [];
    if (metrics.dupCount > 0) {
      list.push({
        id: "duplicates", icon: Copy,
        text: `You have ${metrics.dupCount} duplicate card${metrics.dupCount > 1 ? "s" : ""} worth approximately ${format(metrics.dupValue)}.`,
        actionLabel: "View duplicates", onAction: goDuplicates,
      });
    }
    if (attentionCount > 0) {
      list.push({
        id: "review", icon: ScanSearch,
        text: `Review ${attentionCount} AI match${attentionCount > 1 ? "es" : ""} to keep your collection accurate.`,
        actionLabel: "Review now", onAction: goReviewAttention,
      });
    }
    for (const col of collections) {
      if (col.checklist_status !== "found" || !col.checklist?.length) continue;
      const owned = new Set(cards.filter((c) => c.collection_id === col.id).map((c) => normalizeNumber(c.card_number)));
      const missing = col.checklist.filter((e) => !owned.has(normalizeNumber(e.card_number))).length;
      if (missing > 0 && missing <= 15) {
        list.push({
          id: `set-${col.id}`, icon: Target,
          text: `You're only ${missing} card${missing > 1 ? "s" : ""} away from completing ${col.name}.`,
          actionLabel: "View set", onAction: () => setCollectionFilter(col.id),
        });
        break;
      }
    }
    const trendOf = (c) => (c.is_graded ? c.sold_graded_trend : c.sold_raw_trend);
    const movers = cards.filter((c) => trendOf(c) != null && Math.abs(trendOf(c)) >= 5);
    if (movers.length) {
      const top = movers.reduce((a, b) => (Math.abs(trendOf(b)) > Math.abs(trendOf(a)) ? b : a));
      const t = trendOf(top);
      list.push({
        id: "mover", icon: TrendingUp,
        text: `${top.name} ${t > 0 ? "increased" : "dropped"} ${Math.abs(Math.round(t))}% in recent completed sales.`,
        actionLabel: "View card", to: `/card/${top.id}`,
      });
    }
    return list.slice(0, 4);
  }, [cards, collections, metrics, attentionCount, format]);

  const selectedCollection = useMemo(
    () => (collectionFilter !== "all" && collectionFilter !== "uncategorized" ? collections.find((c) => c.id === collectionFilter) : null),
    [collections, collectionFilter]
  );

  const missingEntries = useMemo(() => {
    if (!selectedCollection || selectedCollection.checklist_status === "none" || ownedOnly) return [];
    const ownedNumbers = new Set(
      cards.filter((c) => c.collection_id === selectedCollection.id).map((c) => normalizeNumber(c.card_number))
    );
    return selectedCollection.checklist.filter((e) => !ownedNumbers.has(normalizeNumber(e.card_number)));
  }, [selectedCollection, cards, ownedOnly]);

  const createCollection = async () => {
    if (!newCollectionName.trim()) return;
    await api.post("/collections", { name: newCollectionName.trim() });
    setNewCollectionName("");
    setShowNewCollection(false);
    toast.success("Collection created");
    loadAll();
  };

  const gameTypes = useMemo(() => Array.from(new Set(cards.map((c) => c.game_type))), [cards]);

  const collectionCounts = useMemo(() => {
    const counts = {};
    for (const c of cards) {
      const key = c.collection_id || "uncategorized";
      counts[key] = (counts[key] || 0) + 1;
    }
    return counts;
  }, [cards]);

  // Sub-categories: the auto-created set collections that contain cards of whichever game type
  // is selected (e.g. click "Pokemon" -> "Pokemon Jungle", "Pokemon Base Set"...). Selecting one
  // opens the real collection view, complete with the checklist / missing-cards panel.
  const collectionsForGameType = useMemo(() => {
    if (gameFilter === "all") return [];
    const ids = new Set(
      cards.filter((c) => c.game_type === gameFilter && c.collection_id).map((c) => c.collection_id)
    );
    return collections.filter((c) => ids.has(c.id));
  }, [cards, collections, gameFilter]);

  const handleGameFilterChange = (value) => {
    setGameFilter(value);
    setCollectionFilter("all");
  };

  const handleDownloadScans = async () => {
    setDownloadingScans(true);
    try {
      const res = await api.get("/cards/export/images", { responseType: "blob", timeout: 0 });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "cardark_scans.zip";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Couldn't build the zip. Please try again.");
    } finally {
      setDownloadingScans(false);
    }
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/cards/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "cardark_collection.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const exportDuplicates = () => {
    const dups = metrics.dupEntries;
    if (!dups.length) return;
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [
      ["Name", "Set", "Number", "Spare copies", "Est. value per copy (USD)"],
      ...dups.map((c) => [
        c.name, c.set_name || "", c.card_number || "", (c.quantity || 1) - 1,
        (((cardTotalValue(c) || 0) / (c.quantity || 1)) || 0).toFixed(2),
      ]),
    ];
    const csv = rows.map((r) => r.map(esc).join(",")).join("\n");
    const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "cardark_duplicates.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    toast.success("Duplicates list exported");
  };

  const prepareEbay = () =>
    toast.info("eBay listing prep is coming soon — Export gives you a ready-to-list CSV in the meantime.");

  const toggleSelect = (cardId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(cardId) ? next.delete(cardId) : next.add(cardId);
      return next;
    });
  };

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  };

  const handleRescanSelected = async () => {
    if (selectedIds.size === 0) return;
    setRescanning(true);
    try {
      await api.post("/cards/rescan", { card_ids: Array.from(selectedIds) });
      toast.success(`Re-scanned ${selectedIds.size} card${selectedIds.size > 1 ? "s" : ""}`);
      exitSelectionMode();
      loadAll();
      checkAuth();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Re-scan failed. Please try again.");
    } finally {
      setRescanning(false);
    }
  };

  const allFilteredSelected = filtered.length > 0 && filtered.every((c) => selectedIds.has(c.id));

  const confirmCards = async (ids) => {
    // verify:true = "I eyeballed these, the matches are right" - clears the needs-attention flag.
    setConfirming(true);
    try {
      await api.post("/cards/confirm", { card_ids: ids, verify: true });
      const idSet = new Set(ids);
      setCards((prev) => prev.map((c) => (idSet.has(c.id) ? { ...c, status: "confirmed", match_verified: true } : c)));
      toast.success(`Confirmed ${ids.length} card${ids.length > 1 ? "s" : ""}`);
      const remaining = cards.filter((c) => !idSet.has(c.id) && needsAttention(c)).length;
      if (remaining === 0) setAttentionOnly(false);
      return true;
    } catch {
      toast.error("Couldn't confirm. Please try again.");
      return false;
    } finally {
      setConfirming(false);
    }
  };

  const handleConfirmSelected = async () => {
    if (selectedIds.size === 0) return;
    const ok = await confirmCards(Array.from(selectedIds));
    if (ok) exitSelectionMode();
  };

  const handleConfirmAllAttention = () => confirmCards(filtered.map((c) => c.id));

  const toggleSelectAll = () => {
    setSelectedIds(allFilteredSelected ? new Set() : new Set(filtered.map((c) => c.id)));
  };

  const handleDeleteCard = async (cardId) => {
    try {
      const res = await api.delete(`/cards/${cardId}`);
      setCards((prev) => prev.filter((c) => c.id !== cardId));
      toast.success("Card removed from your collection");
      if (res.data.removed_collection) {
        setCollections((prev) => prev.filter((c) => c.id !== res.data.removed_collection));
        if (collectionFilter === res.data.removed_collection) setCollectionFilter("all");
      }
      const statsRes = await api.get("/stats");
      setStats(statsRes.data);
    } catch {
      toast.error("Failed to remove card");
    }
  };

  const handleRemoveMatch = async (cardId) => {
    try {
      const res = await api.post(`/cards/${cardId}/remove-match`);
      setCards((prev) => prev.map((c) => (c.id === cardId ? res.data : c)));
      toast.success("Match removed — your scan is now shown");
    } catch {
      toast.error("Couldn't remove the match");
    }
  };

  const cycleSaleStatus = async (cardId) => {
    const card = cards.find((c) => c.id === cardId);
    const order = [null, "under_offer", "sold_stc"];
    const next = order[(order.indexOf(card?.sale_status || null) + 1) % order.length];
    try {
      const res = await api.patch(`/cards/${cardId}`, { sale_status: next || "none" });
      setCards((prev) => prev.map((c) => (c.id === cardId ? res.data : c)));
      toast.success(next === "under_offer" ? "Marked Under Offer" : next === "sold_stc" ? "Marked Sold STC (subject to completion)" : "Card is available again");
    } catch {
      toast.error("Couldn't update sale status");
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    setDeleting(true);
    try {
      const results = await Promise.all(Array.from(selectedIds).map((id) => api.delete(`/cards/${id}`)));
      if (results.some((r) => r.data.removed_collection === collectionFilter)) setCollectionFilter("all");
      toast.success(`Deleted ${selectedIds.size} card${selectedIds.size > 1 ? "s" : ""}`);
      exitSelectionMode();
      setShowDeleteConfirm(false);
      loadAll();
    } catch {
      toast.error("Delete failed. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const clearFilters = () => {
    setSearch("");
    setGameFilter("all");
    setCollectionFilter("all");
    setAttentionOnly(false);
    setQuickFilter(null);
  };

  const emptyState = () => {
    if (cards.length === 0) {
      return { text: "No cards yet? Scan your first collection.", cta: <Link to="/upload"><Button className="bg-[#7C3AED] hover:bg-[#6D28D9] rounded-full px-6">Scan your first stack</Button></Link> };
    }
    if (quickFilter === "duplicates") {
      return { text: "No duplicates? Great! Every card in your collection is unique.", cta: <Button variant="outline" onClick={clearFilters} className="border-white/20 hover:bg-white/5 rounded-full">Show all cards</Button> };
    }
    if (attentionOnly) {
      return { text: "All caught up — no cards need AI review right now.", cta: <Button variant="outline" onClick={clearFilters} className="border-white/20 hover:bg-white/5 rounded-full">Show all cards</Button> };
    }
    return { text: "No cards match your filters.", cta: <Button variant="outline" onClick={clearFilters} className="border-white/20 hover:bg-white/5 rounded-full">Clear filters</Button> };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#05060f] text-white px-6 py-12">
        <div className="max-w-7xl mx-auto animate-pulse" data-testid="dashboard-skeleton">
          <div className="h-9 w-72 bg-white/5 rounded-lg mb-3" />
          <div className="h-4 w-96 max-w-full bg-white/5 rounded mb-10" />
          <div className="h-44 bg-white/5 rounded-2xl mb-6" />
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
            {[...Array(5)].map((_, i) => <div key={i} className="h-28 bg-white/5 rounded-xl" />)}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
            {[...Array(10)].map((_, i) => <div key={i} className="aspect-[3/4.6] bg-white/5 rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#05060f] text-white px-6 py-12">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
          <div>
            <h1 data-testid="dashboard-title" className="font-display text-3xl md:text-4xl font-bold mb-1">Collection Dashboard</h1>
            <p className="text-zinc-400 text-sm">Track, organise and grow your trading card collection.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {selectionMode ? (
              <>
                <Button
                  data-testid="binder-select-all-button"
                  variant="outline"
                  size="sm"
                  onClick={toggleSelectAll}
                  className="border-white/20 hover:bg-white/5 rounded-full"
                >
                  <CheckSquare className="w-4 h-4 mr-2" /> {allFilteredSelected ? "Deselect all" : "Select all"}
                </Button>
                <span data-testid="binder-selected-count" className="text-sm text-zinc-400">{selectedIds.size} selected</span>
                <Button
                  data-testid="binder-confirm-selected-button"
                  onClick={handleConfirmSelected}
                  disabled={selectedIds.size === 0 || confirming}
                  variant="outline"
                  className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 hover:text-emerald-200 rounded-full px-6 disabled:opacity-40"
                >
                  <ShieldCheck className="w-4 h-4 mr-2" />
                  {confirming ? "Confirming..." : "Confirm selected"}
                </Button>
                <Button
                  data-testid="binder-rescan-selected-button"
                  onClick={handleRescanSelected}
                  disabled={selectedIds.size === 0 || rescanning}
                  className="bg-[#A78BFA] text-[#05060f] hover:bg-[#8B5CF6] rounded-full px-6 disabled:opacity-40"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${rescanning ? "animate-spin" : ""}`} />
                  {rescanning ? "Re-scanning..." : "Re-scan selected"}
                </Button>
                <Button
                  data-testid="binder-delete-selected-button"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={selectedIds.size === 0 || deleting}
                  variant="outline"
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-full px-6 disabled:opacity-40"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Delete selected
                </Button>
                <Button data-testid="binder-cancel-selection-button" variant="outline" onClick={exitSelectionMode} className="border-white/20 hover:bg-white/5 rounded-full">
                  <X className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                <Button data-testid="binder-select-mode-button" variant="outline" onClick={() => setSelectionMode(true)} className="border-white/20 hover:bg-white/5 rounded-full">
                  <ListChecks className="w-4 h-4 mr-2" /> Select
                </Button>
                <Button data-testid="binder-export-csv-button" variant="outline" onClick={handleExportCsv} disabled={exporting} className="border-white/20 hover:bg-white/5 rounded-full disabled:opacity-50">
                  <Download className="w-4 h-4 mr-2" /> {exporting ? "Exporting..." : "Export CSV"}
                </Button>
                <Button data-testid="binder-download-scans-button" variant="outline" onClick={handleDownloadScans} disabled={downloadingScans} className="border-white/20 hover:bg-white/5 rounded-full disabled:opacity-50">
                  <FileArchive className="w-4 h-4 mr-2" /> {downloadingScans ? "Zipping..." : "Download scans"}
                </Button>
                <ManualAddDialog collections={collections} onAdded={loadAll} />
                <ShowcaseDialog />
                <Link to="/list-ebay">
                  <Button data-testid="binder-list-ebay-button" variant="outline" className="border-[#A78BFA]/40 text-[#A78BFA] hover:bg-[#A78BFA]/10 hover:text-white rounded-full">
                    <Link2 className="w-4 h-4 mr-2" /> List on eBay
                  </Button>
                </Link>
                <Link to="/upload">
                  <Button data-testid="binder-scan-more-button" className="bg-[#7C3AED] hover:bg-[#6D28D9] rounded-full px-6">
                    <UploadCloud className="w-4 h-4 mr-2" /> Scan more cards
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>

        <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <AlertDialogContent data-testid="binder-delete-confirm-dialog" className="bg-[#111113] border-white/10 text-white">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {selectedIds.size} card{selectedIds.size > 1 ? "s" : ""}?</AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                This permanently removes the selected card{selectedIds.size > 1 ? "s" : ""} from your collection. This can't be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="binder-delete-confirm-cancel" className="border-white/20 bg-transparent hover:bg-white/5 text-white">Cancel</AlertDialogCancel>
              <AlertDialogAction
                data-testid="binder-delete-confirm-action"
                onClick={handleDeleteSelected}
                disabled={deleting}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                {deleting ? "Deleting..." : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* ---- CardArk AI insight ticker ---- */}
        <AiSaysCard />

        {/* ---- Hero: estimated market value ---- */}
        <div data-testid="dashboard-hero" className="relative overflow-hidden border border-white/10 bg-[#111113] rounded-2xl p-6 sm:p-8 mb-6">
          <div className="absolute -top-28 -right-20 w-80 h-80 bg-[#7C3AED]/20 rounded-full blur-[110px] pointer-events-none" />
          <div className="relative flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 flex items-center gap-2 mb-3">
                <Wallet className="w-3.5 h-3.5 text-[#F59E0B]" /> Estimated Market Value
              </p>
              <AnimatedNumber
                value={stats?.total_value ?? 0}
                format={format}
                testId="dashboard-market-value"
                className="font-display text-5xl sm:text-6xl font-bold text-white leading-none"
              />
              <p className="text-xs text-zinc-500 mt-3">Based on recent completed sales and market estimates.</p>
              {metrics.pendingCount > 0 && (
                <p data-testid="dashboard-pending-sales" className="text-xs mt-2 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#7C3AED]" />
                  <span className="text-[#A78BFA] font-semibold">{format(metrics.pendingValue)}</span>
                  <span className="text-zinc-500">pending in sales ({metrics.pendingCount} card{metrics.pendingCount > 1 ? "s" : ""} Under Offer / Sold STC)</span>
                  <Link to="/sales" data-testid="dashboard-sales-link" className="text-[#A78BFA] hover:underline">· Sales Ledger →</Link>
                </p>
              )}
            </div>
            {growth && (
              <div data-testid="dashboard-growth-chip" className="flex flex-col items-start gap-1">
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-300 bg-emerald-500/10 border border-emerald-400/25 rounded-full px-4 py-1.5">
                  <TrendingUp className="w-4 h-4" /> {growth.label}
                </span>
                <span className="text-[10px] text-zinc-600 pl-1">{growth.sub}</span>
              </div>
            )}
          </div>
        </div>

        {/* ---- Stat cards ---- */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-6">
          <StatCard testId="stat-total-cards" icon={Layers} label="Total Cards" value={metrics.totalQty} />
          <StatCard testId="stat-unique-cards" icon={Sparkles} label="Unique Cards" value={metrics.unique} accent="#F59E0B" />
          <StatCard testId="stat-duplicate-cards" icon={Copy} label="Duplicate Cards" value={metrics.dupCount} accent="#38BDF8" />
          <div
            data-testid="stat-ai-review"
            className="border border-white/10 bg-[#111113] rounded-xl p-5 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-[#A78BFA]/30 col-span-2 md:col-span-1"
          >
            <ScanSearch className="w-5 h-5 mb-2 text-[#A78BFA]" />
            <AnimatedNumber value={attentionCount} className="text-2xl font-display font-bold text-white" />
            <p className="text-xs text-zinc-500 mt-0.5">AI Review Needed</p>
            {attentionCount > 0 ? (
              <>
                <p className="text-[10px] text-zinc-600 mt-1 leading-snug">Multiple possible matches were detected for these cards.</p>
                <button
                  data-testid="stat-review-now-button"
                  onClick={goReviewAttention}
                  className="mt-2 text-xs font-semibold px-3 py-1 rounded-full bg-[#A78BFA]/15 text-[#A78BFA] border border-[#A78BFA]/30 hover:bg-[#A78BFA]/25 transition-colors"
                >
                  Review Now
                </button>
              </>
            ) : (
              <p className="text-[10px] text-zinc-600 mt-1">All matches confirmed — nice work.</p>
            )}
          </div>
          {growth ? (
            <StatCard testId="stat-growth" icon={Clock} label="Collection Growth" value={metrics.weekCount} accent="#34D399" sub={growth.label} />
          ) : (
            <div className="hidden lg:block" />
          )}
        </div>

        {/* ---- Insights + duplicates ---- */}
        <div className={`grid grid-cols-1 ${insights.length ? "lg:grid-cols-2" : ""} gap-4 mb-8`}>
          <InsightsPanel insights={insights} />
          <DuplicatesPanel
            count={metrics.dupCount}
            value={metrics.dupValue}
            format={format}
            onExport={exportDuplicates}
            onPrepareEbay={prepareEbay}
            onViewDuplicates={goDuplicates}
          />
        </div>

        <TopMoversStrip />

        <SalesLedgerStrip />

        <ChaseDeckStrip />

        {/* ---- Search, filters & sort ---- */}
        <div ref={filtersRef} className="flex flex-col md:flex-row gap-4 mb-4 scroll-mt-24">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              data-testid="binder-search-input"
              placeholder="Search your collection..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-[#111113] border-white/10 rounded-full"
            />
          </div>
          <Select value={collectionFilter} onValueChange={setCollectionFilter}>
            <SelectTrigger data-testid="binder-collection-select" className="w-full md:w-56 bg-[#111113] border-white/10 rounded-full shrink-0">
              <SelectValue placeholder="All collections" />
            </SelectTrigger>
            <SelectContent className="max-h-80 bg-[#111113] border-white/10 text-white">
              <SelectItem data-testid="collection-option-all" value="all">All collections ({cards.length})</SelectItem>
              <SelectItem data-testid="collection-option-uncategorized" value="uncategorized">Uncategorized ({collectionCounts["uncategorized"] || 0})</SelectItem>
              {collections.map((c) => (
                <SelectItem key={c.id} data-testid={`collection-option-${c.id}`} value={c.id}>
                  {c.name} ({collectionCounts[c.id] || 0})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger data-testid="binder-sort-select" className="w-full md:w-48 bg-[#111113] border-white/10 rounded-full shrink-0">
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent className="bg-[#111113] border-white/10 text-white">
              <SelectItem data-testid="sort-option-newest" value="newest">Newest first</SelectItem>
              <SelectItem data-testid="sort-option-value-desc" value="value_desc">Value: high to low</SelectItem>
              <SelectItem data-testid="sort-option-value-asc" value="value_asc">Value: low to high</SelectItem>
              <SelectItem data-testid="sort-option-name" value="name">Name A–Z</SelectItem>
            </SelectContent>
          </Select>
          {showNewCollection ? (
            <div className="flex gap-2">
              <Input
                data-testid="binder-new-collection-input"
                autoFocus
                placeholder="Collection name"
                value={newCollectionName}
                onChange={(e) => setNewCollectionName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && createCollection()}
                className="bg-[#111113] border-white/10 rounded-full w-40"
              />
              <Button data-testid="binder-save-collection-button" size="sm" onClick={createCollection} className="bg-[#A78BFA] text-[#05060f] hover:bg-[#8B5CF6] rounded-full">Add</Button>
            </div>
          ) : (
            <Button data-testid="binder-new-collection-button" variant="outline" onClick={() => setShowNewCollection(true)} className="border-white/20 hover:bg-white/5 rounded-full">
              <Plus className="w-4 h-4 mr-1" /> Collection
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <button data-testid="game-filter-all" onClick={() => handleGameFilterChange("all")} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${gameFilter === "all" ? "border-[#A78BFA] text-[#A78BFA] bg-[#A78BFA]/10" : "border-white/10 text-zinc-400 hover:text-white"}`}>All types</button>
          {gameTypes.map((g) => (
            <button key={g} data-testid={`game-filter-${g}`} onClick={() => handleGameFilterChange(g)} className={`text-xs px-3 py-1.5 rounded-full border capitalize transition-colors ${gameFilter === g ? "border-[#A78BFA] text-[#A78BFA] bg-[#A78BFA]/10" : "border-white/10 text-zinc-400 hover:text-white"}`}>{g}</button>
          ))}
          <span className="w-px h-4 bg-white/10 mx-1" />
          <button data-testid="quick-filter-duplicates" onClick={() => setQuickFilter(quickFilter === "duplicates" ? null : "duplicates")} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${quickFilter === "duplicates" ? "border-sky-400 text-sky-300 bg-sky-500/10" : "border-white/10 text-zinc-400 hover:text-white"}`}>Duplicates</button>
          <button data-testid="quick-filter-verified" onClick={() => setQuickFilter(quickFilter === "verified" ? null : "verified")} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${quickFilter === "verified" ? "border-emerald-400 text-emerald-300 bg-emerald-500/10" : "border-white/10 text-zinc-400 hover:text-white"}`}>Verified</button>
          <button data-testid="quick-filter-recent" onClick={() => setQuickFilter(quickFilter === "recent" ? null : "recent")} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${quickFilter === "recent" ? "border-[#F59E0B] text-[#F59E0B] bg-[#F59E0B]/10" : "border-white/10 text-zinc-400 hover:text-white"}`}>Recently added</button>
          {attentionCount > 0 && (
            <button
              data-testid="needs-attention-filter"
              onClick={() => setAttentionOnly((v) => !v)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors flex items-center gap-1.5 ${attentionOnly ? "border-[#F59E0B] text-[#F59E0B] bg-[#F59E0B]/10" : "border-[#F59E0B]/30 text-[#F59E0B]/80 hover:text-[#F59E0B]"}`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B]" /> AI Review Needed ({attentionCount})
            </button>
          )}
          {attentionOnly && filtered.length > 0 && (
            <button
              data-testid="confirm-all-attention-button"
              onClick={handleConfirmAllAttention}
              disabled={confirming}
              title="Mark every card shown as reviewed & correct"
              className="text-xs px-3 py-1.5 rounded-full border border-emerald-400/40 text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 transition-colors flex items-center gap-1.5 disabled:opacity-50"
            >
              <ShieldCheck className="w-3.5 h-3.5" />
              {confirming ? "Confirming..." : `Confirm all (${filtered.length})`}
            </button>
          )}
          <span data-testid="binder-result-count" className="ml-auto text-xs text-zinc-500">
            {filtered.length === cards.length
              ? `${cards.length} card${cards.length === 1 ? "" : "s"}`
              : `${filtered.length} of ${cards.length} cards`}
          </span>
        </div>

        {collectionsForGameType.length > 0 && (
          <div data-testid="binder-set-filter-row" className="flex gap-2 mb-8 flex-wrap">
            <button
              data-testid="set-filter-all"
              onClick={() => setCollectionFilter("all")}
              className={`text-[11px] px-3 py-1 rounded-full border transition-colors ${collectionFilter === "all" ? "border-[#F59E0B] text-[#F59E0B] bg-[#F59E0B]/10" : "border-white/10 text-zinc-500 hover:text-white"}`}
            >
              All sets
            </button>
            {collectionsForGameType.map((c) => (
              <button
                key={c.id}
                data-testid={`set-filter-${c.id}`}
                onClick={() => setCollectionFilter(c.id)}
                className={`text-[11px] px-3 py-1 rounded-full border transition-colors ${collectionFilter === c.id ? "border-[#F59E0B] text-[#F59E0B] bg-[#F59E0B]/10" : "border-white/10 text-zinc-500 hover:text-white"}`}
              >
                {c.name}
              </button>
            ))}
          </div>
        )}

        {selectedCollection && (
          <ChecklistPanel
            collection={selectedCollection}
            ownedOnly={ownedOnly}
            onToggleOwnedOnly={setOwnedOnly}
            onUpdated={loadAll}
          />
        )}

        {filtered.length === 0 && missingEntries.length === 0 ? (
          <div data-testid="binder-empty-state" className="text-center py-24 border border-dashed border-white/10 rounded-2xl">
            <p className="text-zinc-500 mb-4">{emptyState().text}</p>
            {emptyState().cta}
          </div>
        ) : (
          <div data-testid="binder-card-grid" className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
            {filtered.map((card, i) => (
              <CardTile
                key={card.id}
                card={card}
                index={i}
                selectable={selectionMode}
                selected={selectedIds.has(card.id)}
                onToggleSelect={toggleSelect}
                onDelete={handleDeleteCard}
                onRemoveMatch={handleRemoveMatch}
                onSaleStatus={cycleSaleStatus}
              />
            ))}
            {missingEntries.map((entry) => (
              <MissingCardTile key={entry.card_number} entry={entry} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
